/**
 * Step 1: Fitness Goal - Training Cycle Selection
 * Uses admin-defined macro presets when available
 */
import React from 'react';
import { Target, CheckCircle, Dumbbell, Zap, TrendingUp, Scale, Sparkles, RefreshCw } from 'lucide-react';

const CYCLE_TYPES = [
  { 
    id: 'aufbau', 
    label: 'Aufbau-Phase', 
    icon: Dumbbell, 
    adjustment: 15, 
    description: 'Muskelaufbau mit moderatem Überschuss',
    color: 'amber',
    macros: { protein: 30, carbs: 45, fat: 25 },
    protein_per_kg: 2.0
  },
  { 
    id: 'kraft', 
    label: 'Kraft-Phase', 
    icon: Zap, 
    adjustment: 5, 
    description: 'Kraftsteigerung mit leichtem Überschuss',
    color: 'orange',
    macros: { protein: 30, carbs: 40, fat: 30 },
    protein_per_kg: 2.0
  },
  { 
    id: 'masse', 
    label: 'Masse-Phase', 
    icon: TrendingUp, 
    adjustment: 20, 
    description: 'Maximaler Muskelaufbau',
    color: 'red',
    macros: { protein: 25, carbs: 50, fat: 25 },
    protein_per_kg: 2.0
  },
  { 
    id: 'erhaltung', 
    label: 'Erhaltung', 
    icon: Scale, 
    adjustment: 0, 
    description: 'Gewicht & Form halten',
    color: 'green',
    macros: { protein: 25, carbs: 50, fat: 25 },
    protein_per_kg: 1.6
  },
  { 
    id: 'leichte_definition', 
    label: 'Leichte Definition', 
    icon: Sparkles, 
    adjustment: -10, 
    description: 'Sanfter Fettabbau',
    color: 'teal',
    macros: { protein: 30, carbs: 40, fat: 30 },
    protein_per_kg: 2.0
  },
  { 
    id: 'recomp', 
    label: 'Body Recomposition', 
    icon: RefreshCw, 
    adjustment: -15, 
    description: 'Leichte Definition & Muskelerhalt',
    color: 'cyan',
    macros: { protein: 30, carbs: 35, fat: 35 },
    protein_per_kg: 2.2
  },
  { 
    id: 'definition', 
    label: 'Definitions-Phase', 
    icon: Target, 
    adjustment: -20, 
    description: 'Klassisches Abnehmen',
    color: 'blue',
    macros: { protein: 30, carbs: 40, fat: 30 },
    protein_per_kg: 2.0
  },
];

const StepFitnessGoal = ({ prefs, setPrefs, calcSettings, dataLoaded }) => {
  // Get admin-defined macro presets if available
  const adminMacroPresets = calcSettings?.macro_presets_by_goal || {};
  
  // Get macros for a cycle type (admin preset or default)
  const getMacrosForCycle = (cycleId, defaultMacros) => {
    const adminPreset = adminMacroPresets[cycleId];
    if (adminPreset && adminPreset.protein_percent && adminPreset.carbs_percent && adminPreset.fat_percent) {
      return {
        protein: adminPreset.protein_percent,
        carbs: adminPreset.carbs_percent,
        fat: adminPreset.fat_percent
      };
    }
    return defaultMacros;
  };
  
  return (
    <div className="space-y-4" data-testid="onboarding-step-fitness-goal">
      <div className="text-center mb-6">
        <Target className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-white">Was ist dein Ziel?</h3>
        <p className="text-zinc-400 text-sm">Wähle deinen aktuellen Trainings-Zyklus</p>
        {!dataLoaded && (
          <p className="text-xs text-zinc-500 mt-1 animate-pulse">Gespeicherte Daten werden geladen...</p>
        )}
      </div>
      
      <div className="space-y-3">
        {CYCLE_TYPES.map(cycle => {
          const macros = getMacrosForCycle(cycle.id, cycle.macros);
          
          return (
            <button
              key={cycle.id}
              data-testid={`onboarding-cycle-${cycle.id}`}
              onClick={() => setPrefs(prev => ({ ...prev, cycle_type: cycle.id, fitness_goal: cycle.id, protein_per_kg: cycle.protein_per_kg }))}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                prefs.cycle_type === cycle.id
                  ? 'border-amber-500 bg-amber-500/10'
                  : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
              }`}
            >
              <div className="flex items-center gap-4">
                <cycle.icon className="w-8 h-8 text-amber-400 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-white text-lg">{cycle.label}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      cycle.adjustment > 0 ? 'bg-green-500/20 text-green-400' : 
                      cycle.adjustment < 0 ? 'bg-blue-500/20 text-blue-400' : 
                      'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {cycle.adjustment > 0 ? '+' : ''}{cycle.adjustment}% kcal
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm">{cycle.description}</p>
                  {/* Show macro distribution */}
                  <div className="flex gap-2 mt-2 text-xs">
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded">
                      P: {macros.protein}%
                    </span>
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded">
                      KH: {macros.carbs}%
                    </span>
                    <span className="px-2 py-0.5 bg-orange-500/20 text-orange-300 rounded">
                      F: {macros.fat}%
                    </span>
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded">
                      {cycle.protein_per_kg}g/kg
                    </span>
                  </div>
                </div>
                {prefs.cycle_type === cycle.id && (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { CYCLE_TYPES };
export default StepFitnessGoal;
